import { createContext, useContext } from "react";
import type { Category, Check, Favorite, Me, Meta, Product } from "./api/types";
import { emptyPiece, type PieceFields } from "./lib/piece";

/** The piece that fits you well: a saved favorite, or a newly pasted product plus the size you wear. */
export type RefChoice =
  | { kind: "fav"; fav: Favorite }
  | { kind: "new"; product: Product; size: string | null; variant: string | null; wouldChange: string[]; fitWish?: string; saveAsFavorite: boolean };

export type BoardDraft = {
  candRaw: string;
  cand: Product | null;
  tappedCat: Category | null;
  refRaw: string;
  ref: RefChoice | null;
  check: Check | null;
};

export const emptyDraft = (): BoardDraft => ({ candRaw: "", cand: null, tappedCat: null, refRaw: "", ref: null, check: null });

/** The favorite being described in step 1, kept while the shopper hops between steps. `savedId` is the
 *  favorite it last saved as, so saving again replaces that one instead of adding a second. */
export type FavDraft = { cat: Category; kind: string; fields: PieceFields; savedId: string | null };
export const emptyFavDraft = (): FavDraft => ({ cat: "pants", kind: "jeans", fields: emptyPiece("jeans"), savedId: null });

export type AppCtx = {
  me: Me;
  meta: Meta;
  setMe: (m: Me) => void;
  draft: BoardDraft;
  setDraft: (f: (d: BoardDraft) => BoardDraft) => void;
  newBoard: () => void;
  fav: FavDraft;
  setFav: (f: (d: FavDraft) => FavDraft) => void;
};

export const Ctx = createContext<AppCtx | null>(null);

export function useApp(): AppCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useApp outside provider");
  return c;
}

const SKIP_KEY = "drape.onboarding.skipped";
const WELCOME_KEY = "drape.welcomed";
function flag(k: string): boolean {
  try {
    return localStorage.getItem(k) === "1";
  } catch {
    return false;
  }
}
function setFlag(k: string) {
  try {
    localStorage.setItem(k, "1");
  } catch {
    /* private mode */
  }
}
export const onboardingSkipped = () => flag(SKIP_KEY);
export const markOnboardingSkipped = () => setFlag(SKIP_KEY);
export const welcomed = () => flag(WELCOME_KEY);
export const markWelcomed = () => setFlag(WELCOME_KEY);

export function needsOnboarding(me: Me): boolean {
  return !me.user.onboarded && me.user.height_cm == null && !onboardingSkipped();
}
